import {
  FullStackElementFramework,
  FullStackElementLanguage,
  FullStackElementOption,
  FullStackElementTree,
  FullStackElementVariant,
  FullStackElementVersion,
} from "@hub/models/content/full-stack-element-tree";
import { VariantVisibility } from "@hub/models/elements/variant-visibility";
import React, { useState } from "react";

export const useFrameworkChooseHook = () => {
  const NOT_APPLICABLE = "not_applicable";

  const [versionOptions, setVersionOptions] = useState<
    FullStackElementVersion[]
  >([]);
  const [DocumentPickerLanguageOptions, setDocumentPickerLanguageOptions] =
    useState<FullStackElementLanguage[]>([]);
  const [variantOptions, setVariantOptions] = useState<
    FullStackElementVariant[]
  >([]);

  const [selectedFramework, setSelectedFramework] =
    useState<FullStackElementFramework | null>(null);
  const [selectedVersion, setSelectedVersion] =
    useState<FullStackElementVersion | null>(null);
  const [selectedLanguage, setSelectedLanguage] =
    useState<FullStackElementLanguage | null>(null);
  const [selectedVariant, setSelectedVariant] =
    useState<FullStackElementVariant | null>(null);

  const canCompleteFrameworkSelection =
    selectedFramework && selectedVersion && selectedLanguage && selectedVariant;

  const handleVariationSelection = (params: {
    elementTree: FullStackElementTree;
    selections: { framework: string; version: string; language: string };
  }) => {
    const { elementTree, selections } = params;
    const { framework, version, language } = selections;

    const variantLeaves =
      elementTree.frameworks[framework]?.versions[version]?.languages[language]
        ?.variants || [];

    setVariantOptions(variantLeaves);

    if (variantLeaves && variantLeaves.length === 0) {
      setSelectedVariant({
        key: NOT_APPLICABLE,
        name: NOT_APPLICABLE,
        inPath: VariantVisibility.HIDDEN,
      });
    }

    if (variantLeaves && variantLeaves.length === 1) {
      const defaultVariant = variantOptions[0];

      if (defaultVariant) {
        setSelectedVariant(defaultVariant);
      }
    }
  };

  const handleLanguageSelection = (params: {
    elementTree: FullStackElementTree;
    selections: { framework: string; version: string };
  }) => {
    const { elementTree, selections } = params;
    const { framework, version } = selections;

    const languageSubtree =
      elementTree.frameworks[framework]?.versions[version]?.languages;

    if (languageSubtree) {
      const languages: FullStackElementOption[] = Object.values(
        languageSubtree
      ).map((language) => ({
        key: language.data.key,
        name: language.data.name,
        icon: language.data.icon,
      }));

      setDocumentPickerLanguageOptions(languages);

      const defaultLanguage = languages[0];

      if (defaultLanguage && languages.length === 1) {
        setSelectedLanguage(defaultLanguage);

        handleVariationSelection({
          elementTree,
          selections: {
            framework,
            version: version,
            language: defaultLanguage.key,
          },
        });
      }
    }
  };

  const handleVersionSelection = (params: {
    elementTree: FullStackElementTree;
    selections: { framework: FullStackElementOption };
  }) => {
    const { elementTree, selections } = params;
    const { framework } = selections;

    const elementTreeFramework = elementTree.frameworks[framework.key];

    if (!elementTreeFramework) {
      return;
    }

    const versionSubtree = elementTreeFramework.versions;

    if (versionSubtree) {
      const versions: FullStackElementVersion[] = Object.values(
        versionSubtree
      ).map((version) => ({
        key: version.data.key,
        name: version.data.name,
        status: version.data.status,
      }));
      setVersionOptions(versions);

      const defaultVersion = versions[0];

      if (defaultVersion && versions.length === 1) {
        setSelectedVersion(defaultVersion);

        handleLanguageSelection({
          elementTree,
          selections: {
            framework: framework.key,
            version: defaultVersion.key,
          },
        });
      }
    }
  };

  const onFrameworkSelection = (params: {
    elementTree: FullStackElementTree;
    selections: { framework: FullStackElementOption };
  }) => {
    const { elementTree, selections } = params;
    const { framework } = selections;

    setSelectedFramework(framework);

    setSelectedVersion(null);
    setSelectedLanguage(null);
    setSelectedVariant(null);

    setVersionOptions([]);
    setDocumentPickerLanguageOptions([]);
    setVariantOptions([]);

    handleVersionSelection({ elementTree, selections: { framework } });
  };

  const onVersionSelection = (params: {
    elementTree: FullStackElementTree;
    selections: {
      framework: FullStackElementFramework;
      version: FullStackElementVersion;
    };
  }) => {
    const { elementTree, selections } = params;
    const { framework, version } = selections;

    setSelectedVersion(version);

    setSelectedLanguage(null);
    setSelectedVariant(null);

    setDocumentPickerLanguageOptions([]);
    setVariantOptions([]);

    handleLanguageSelection({
      elementTree,
      selections: { framework: framework.key, version: version.key },
    });
  };

  const onLanguageSelection = (params: {
    elementTree: FullStackElementTree;
    selections: {
      framework: FullStackElementOption;
      version: FullStackElementOption;
      language: FullStackElementOption;
    };
  }) => {
    const { elementTree, selections } = params;
    const { framework, version, language } = selections;

    setSelectedLanguage(language);

    setSelectedVariant(null);

    setVariantOptions([]);

    handleVariationSelection({
      elementTree,
      selections: {
        framework: framework.key,
        version: version.key,
        language: language.key,
      },
    });
  };

  const onVariantSelection = (params: {
    selections: { variant: FullStackElementVariant };
  }) => {
    const { selections } = params;
    const { variant } = selections;

    setSelectedVariant(variant);
  };

  return {
    NOT_APPLICABLE,

    selectedFramework,
    selectedVersion,
    selectedLanguage,
    selectedVariant,

    versionOptions,
    DocumentPickerLanguageOptions,
    variantOptions,

    onFrameworkSelection,
    onVersionSelection,
    onLanguageSelection,
    onVariantSelection,

    canCompleteFrameworkSelection,
  };
};
